import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Search,
  MapPin,
  ExternalLink,
  Star,
  ChevronDown,
  Globe,
  Clock,
  ArrowUp,
  ArrowDown,
  Eye,
  TrendingUp,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

// This component uses REACT_APP_API_URL from .env file for API calls
const SerpChecker = () => {
  const location = useLocation();
  const [keyword, setKeyword] = useState('');
  const [country, setCountry] = useState('gb');
  const [currentPage, setCurrentPage] = useState(1);
  const [maxPage, setMaxPage] = useState(1);
  const [autoSearchDone, setAutoSearchDone] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState('');
  const [serpResults, setSerpResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);

  // Updated countries array with lowercase codes as values
  const countries = [
    { code: 'us', name: 'United States' },
    { code: 'gb', name: 'United Kingdom' },
    { code: 'ca', name: 'Canada' },
    { code: 'ru', name: 'Russia' },
    { code: 'de', name: 'Germany' },
    { code: 'fr', name: 'France' },
    { code: 'es', name: 'Spain' },
    { code: 'it', name: 'Italy' },
    { code: 'br', name: 'Brazil' },
    { code: 'au', name: 'Australia' },
    { code: 'ar', name: 'Argentina' },
    { code: 'be', name: 'Belgium' },
    { code: 'ch', name: 'Switzerland' },
    { code: 'dk', name: 'Denmark' },
    { code: 'fi', name: 'Finland' },
    { code: 'hk', name: 'Hong Kong' },
    { code: 'ie', name: 'Ireland' },
    { code: 'il', name: 'Israel' },
    { code: 'mx', name: 'Mexico' },
    { code: 'nl', name: 'Netherlands' },
    { code: 'no', name: 'Norway' },
    { code: 'pl', name: 'Poland' },
    { code: 'se', name: 'Sweden' },
    { code: 'sg', name: 'Singapore' },
    { code: 'tr', name: 'Turkey' },
    { code: 'jp', name: 'Japan' },
    { code: 'in', name: 'India' },
    { code: 'hu', name: 'Hungary' },
    { code: 'af', name: 'Afghanistan' },
    { code: 'al', name: 'Albania' },
    { code: 'dz', name: 'Algeria' },
    { code: 'ao', name: 'Angola' },
    { code: 'am', name: 'Armenia' },
    { code: 'at', name: 'Austria' },
    { code: 'az', name: 'Azerbaijan' },
    { code: 'bh', name: 'Bahrain' },
    { code: 'bd', name: 'Bangladesh' },
    { code: 'by', name: 'Belarus' },
    { code: 'bz', name: 'Belize' },
    { code: 'bo', name: 'Bolivia' },
    { code: 'ba', name: 'Bosnia and Herzegovina' },
    { code: 'bw', name: 'Botswana' },
    { code: 'bn', name: 'Brunei' },
    { code: 'bg', name: 'Bulgaria' },
    { code: 'cv', name: 'Cabo Verde' },
    { code: 'kh', name: 'Cambodia' },
    { code: 'cm', name: 'Cameroon' },
    { code: 'cl', name: 'Chile' },
    { code: 'co', name: 'Colombia' },
    { code: 'cr', name: 'Costa Rica' },
    { code: 'hr', name: 'Croatia' },
    { code: 'cy', name: 'Cyprus' },
    { code: 'cz', name: 'Czech Republic' },
    { code: 'cd', name: 'Congo' },
    { code: 'do', name: 'Dominican Republic' },
    { code: 'ec', name: 'Ecuador' },
    { code: 'eg', name: 'Egypt' },
    { code: 'sv', name: 'El Salvador' },
    { code: 'ee', name: 'Estonia' },
    { code: 'et', name: 'Ethiopia' },
    { code: 'ge', name: 'Georgia' },
    { code: 'gh', name: 'Ghana' },
    { code: 'gr', name: 'Greece' },
    { code: 'gt', name: 'Guatemala' },
    { code: 'gy', name: 'Guyana' },
    { code: 'ht', name: 'Haiti' },
    { code: 'hn', name: 'Honduras' },
    { code: 'is', name: 'Iceland' },
    { code: 'id', name: 'Indonesia' },
    { code: 'jm', name: 'Jamaica' },
    { code: 'jo', name: 'Jordan' },
    { code: 'kz', name: 'Kazakhstan' },
    { code: 'kw', name: 'Kuwait' },
    { code: 'lv', name: 'Latvia' },
    { code: 'lb', name: 'Lebanon' },
    { code: 'lt', name: 'Lithuania' },
    { code: 'lu', name: 'Luxembourg' },
    { code: 'mg', name: 'Madagascar' },
    { code: 'my', name: 'Malaysia' },
    { code: 'mt', name: 'Malta' },
    { code: 'mu', name: 'Mauritius' },
    { code: 'md', name: 'Moldova' },
    { code: 'mn', name: 'Mongolia' },
    { code: 'me', name: 'Montenegro' },
    { code: 'ma', name: 'Morocco' },
    { code: 'mz', name: 'Mozambique' },
    { code: 'na', name: 'Namibia' },
    { code: 'np', name: 'Nepal' },
    { code: 'nz', name: 'New Zealand' },
    { code: 'ni', name: 'Nicaragua' },
    { code: 'ng', name: 'Nigeria' },
    { code: 'om', name: 'Oman' },
    { code: 'py', name: 'Paraguay' },
    { code: 'pe', name: 'Peru' },
    { code: 'ph', name: 'Philippines' },
    { code: 'pt', name: 'Portugal' },
    { code: 'ro', name: 'Romania' },
    { code: 'sa', name: 'Saudi Arabia' },
    { code: 'sn', name: 'Senegal' },
    { code: 'rs', name: 'Serbia' },
    { code: 'sk', name: 'Slovakia' },
    { code: 'si', name: 'Slovenia' },
    { code: 'za', name: 'South Africa' },
    { code: 'kr', name: 'South Korea' },
    { code: 'lk', name: 'Sri Lanka' },
    { code: 'th', name: 'Thailand' },
    { code: 'bs', name: 'Bahamas' },
    { code: 'tt', name: 'Trinidad and Tobago' },
    { code: 'tn', name: 'Tunisia' },
    { code: 'ua', name: 'Ukraine' },
    { code: 'ae', name: 'United Arab Emirates' },
    { code: 'uy', name: 'Uruguay' },
    { code: 've', name: 'Venezuela' },
    { code: 'vn', name: 'Vietnam' },
    { code: 'zm', name: 'Zambia' },
    { code: 'zw', name: 'Zimbabwe' },
    { code: 'ly', name: 'Libya' }
  ];

  // Flag component using FlagsAPI - convert to uppercase for flag API
  const FlagImage = ({ countryCode, size = 24, className = "" }) => {
    return (
      <img
        src={`https://flagsapi.com/${countryCode.toUpperCase()}/flat/${size}.png`}
        alt={`${countryCode} flag`}
        className={`rounded-sm ${className}`}
        style={{ width: `${size}px`, height: `${size * 0.75}px` }}
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
    );
  };

  // Auto-fill from URL params (when redirected from Keyword Explorer)
  useEffect(() => {
    if (autoSearchDone) return;
    const params = new URLSearchParams(location.search);
    const urlKeyword = params.get('keyword');
    const urlCountry = params.get('country');

    if (urlKeyword) {
      setKeyword(urlKeyword);
      if (urlCountry) {
        setCountry(urlCountry.toLowerCase());
      }
      setAutoSearchDone(true);
      // Auto-trigger search after state updates
      setTimeout(() => {
        document.getElementById('serp-search-btn')?.click();
      }, 300);
    }
  }, [location.search, autoSearchDone]);

  const handleSubmit = async (e, page = 1) => {
    if (e) e.preventDefault();


    setError('');

    if (!keyword.trim()) {
      setError('Please enter a keyword to search');
      return;
    }

    if (keyword.trim().length < 2) {
      setError('Keyword must be at least 2 characters long');
      return;
    }

    setIsLoading(true);
    if (page === 1) {
      setShowResults(false);
      setMaxPage(1); // Reset on fresh search
    }

    try {

      const API_URL = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

      const response = await fetch(`${API_URL}/api/serp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          keyword: keyword.trim(),
          country: country,
          page: page
        }),
      });

      let data = null;
      try {
        data = await response.json();
      } catch (parseErr) {
        // ignore
      }

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        }
        const serverMsg = data?.error || data?.message;
        throw new Error(serverMsg || `Request failed with status ${response.status}`);
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      const results = data?.results || [];
      setSerpResults(results);
      setCurrentPage(page);
      setTotalResults(data?.total_results || results.length || 0);
      setShowResults(true);

      // If this page returned results, unlock the next page button (up to Google's max of 10)
      if (results.length > 0) {
        setMaxPage(prev => Math.min(10, Math.max(prev, page + 1)));
      }

      if (results.length === 0) {
        setError(`No organic results were returned for "${keyword.trim()}" in this location. Try a different keyword or country.`);
      }

    } catch (err) {
      console.error('SERP API Error:', err);

      if (err.message && err.message.includes('Authentication')) {
        setError(err.message);
      } else {
        setError(err.message || 'Unable to fetch SERP results. Please try again.');
      }
      setSerpResults([]);
      setShowResults(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle pagination
  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1 && page <= maxPage + 1) {
      handleSubmit(null, page);
    }
  };

  // Generate page numbers up to the highest known page
  const generatePageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= maxPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  // Classification badge renderer
  const ClassificationBadge = ({ classification, vertical }) => {
    if (!classification) return null;

    let badgeClass = '';
    let label = '';

    switch (classification) {
      case 'affiliate':
        badgeClass = 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
        label = 'Affiliate';
        break;
      case 'brand':
        badgeClass = 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
        label = 'Brand';
        break;
      case 'other':
        badgeClass = 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400';
        label = 'Other';
        break;
      case 'unknown':
        badgeClass = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
        label = 'Classifying...';
        break;
      default:
        return null;
    }

    return (
      <span className="inline-flex items-center gap-1.5 mt-1 mb-1">
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badgeClass}`}>
          {label}
        </span>
        {classification === 'affiliate' && vertical && (
          <span className="text-xs text-gray-500 dark:text-slate-400">{vertical}</span>
        )}
      </span>
    );
  };

  const selectedCountry = countries.find(c => c.code === country);

  return (
    <main className="p-4 min-h-screen bg-gray-100 dark:bg-slate-950" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>

      {/* Header Section */}
      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm">
          <div className="p-6 border-b border-gray-200 dark:border-slate-700">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h1 className="text-2xl font-semibold mb-2 text-gray-800 dark:text-slate-100">
                  SERP Checker
                </h1>
                <p className="text-gray-600 dark:text-slate-400 leading-relaxed max-w-xl">
                  Check keyword rankings and analyze search engine results pages across different locations.
                </p>
              </div>
            </div>
          </div>

          {/* Search Form */}
          <div className="p-6">
            <div className="space-y-4">

              {/* Error Message */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

                {/* Keyword Input */}
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-100 mb-2">
                    Enter a Keyword <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={keyword}
                      onChange={(e) => {
                        setKeyword(e.target.value);
                        if (error) setError('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSubmit();
                        }
                      }}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:border-blue-600 transition-all duration-200 text-gray-800 dark:text-slate-100 dark:bg-slate-800 ${
                        error ? 'border-red-300 dark:border-red-700 focus:ring-red-600' : 'border-gray-200 dark:border-slate-600 focus:ring-blue-600'
                      }`}
                      placeholder="e.g., Online Casino, SEO Tools, Best Restaurants"
                      maxLength={100}
                    />
                  </div>
                  {keyword.length > 0 && (
                    <div className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                      {keyword.length}/100 characters
                    </div>
                  )}
                </div>

                {/* Country Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-100 mb-2">
                    Country
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="w-full flex items-center justify-between px-4 py-3 border border-gray-200 dark:border-slate-600 rounded-lg hover:border-blue-300 focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all duration-200 bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-100"
                    >
                      <div className="flex items-center gap-2">
                        {selectedCountry && (
                          <>
                            <FlagImage countryCode={selectedCountry.code} size={16} />
                            <MapPin className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium">{selectedCountry.name}</span>
                          </>
                        )}
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isDropdownOpen ? 'transform rotate-180' : ''}`} />
                    </button>

                    {isDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {countries.map((countryOption) => (
                          <button
                            key={countryOption.code}
                            type="button"
                            onClick={() => {
                              setCountry(countryOption.code);
                              setIsDropdownOpen(false);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 dark:hover:bg-slate-800 transition-all duration-200 text-left border-b border-gray-100 dark:border-slate-800 last:border-b-0"
                          >
                            <FlagImage countryCode={countryOption.code} size={16} />
                            <span className="text-sm font-medium text-gray-700 dark:text-slate-100">{countryOption.name}</span>
                            {country === countryOption.code && (
                              <CheckCircle className="w-4 h-4 text-blue-600 ml-auto" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-center pt-2">
                <button
                  id="serp-search-btn"
                  type="button"
                  onClick={() => {
                    handleSubmit();
                  }}
                  disabled={isLoading || !keyword.trim()}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white rounded-lg px-8 py-3 transition-all duration-200 hover:shadow-sm font-medium"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Checking...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4" />
                      Check Rankings
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Results Section */}
      {showResults && serpResults.length > 0 && (
        <div className="space-y-4">

          {/* Google-style Search Results */}
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm">
            <div className="p-6 dark:bg-slate-900">
              {/* Search Info */}
              <div className="mb-6 pb-4 border-b border-gray-200 dark:border-slate-700">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400">
                  <Search className="w-4 h-4" />
                  <span>Search results for "{keyword}" in {selectedCountry?.name || country}</span>
                  <span className="text-gray-400 dark:text-slate-600">•</span>
                  <span>{serpResults.length} results found</span>
                  {currentPage > 1 && (
                    <>
                      <span className="text-gray-400 dark:text-slate-600">•</span>
                      <span>Page {currentPage}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Google Search Results Container */}
              <div className="w-full">

                {serpResults.map((result, index) => (
                  <div key={`${currentPage}-${index}`} className="mb-6">

                    {/* Only Organic Results */}
                    {result.type === 'organic' && (
                      <div>
                        {/* URL with Favicon + Classification Badge */}
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <img
                            src={result.favicon_path || result.favicon}
                            alt={`${result.domain || result.displayUrl} favicon`}
                            className="w-4 h-4 rounded-sm flex-shrink-0 object-contain"
                            onError={(e) => {
                              e.target.src = 'https://mars.mxg.co.in/mars_log/assets/img/question.webp';
                            }}
                          />
                          <span className="text-sm text-green-700 dark:text-green-500">{result.displayUrl || result.domain}</span>
                          <ExternalLink className="w-3 h-3 text-gray-400" />
                          <ClassificationBadge
                            classification={result.classification}
                            vertical={result.vertical}
                          />
                        </div>

                        {/* Title - Clickable */}
                        <a
                          href={result.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xl text-blue-700 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline cursor-pointer mb-1 font-normal block"
                        >
                          {result.title}
                        </a>

                        <p className="text-sm text-gray-700 dark:text-slate-300 leading-relaxed mb-3">
                          {result.meta_description || result.description}
                        </p>

                        {/* Horizontal Sitelinks */}
                        {result.sitelinks && result.sitelinks.length > 0 && (
                          <div className="flex flex-wrap gap-4 mb-2">
                            {result.sitelinks.map((sitelink, idx) => (
                              <a key={idx} href="#" className="text-xs text-blue-700 hover:underline">
                                {sitelink.title}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {/* Pagination — only shows known pages */}
                <div className="mt-12 pt-8 border-t border-gray-200 dark:border-slate-700">
                  <div className="flex items-center justify-center">
                    <div className="flex items-center gap-2">
                      {/* Previous button */}
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || isLoading}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-blue-700 hover:bg-blue-50 rounded transition-colors duration-200 disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Previous
                      </button>

                      {/* Page numbers — only known pages */}
                      <div className="flex items-center gap-1 mx-4">
                        {generatePageNumbers().map((pageNum) => (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            disabled={isLoading}
                            className={`w-10 h-10 flex items-center justify-center text-sm rounded-full transition-colors duration-200 ${
                              currentPage === pageNum
                                ? 'bg-blue-600 text-white'
                                : 'text-blue-700 hover:bg-blue-50 disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:bg-transparent'
                            }`}
                          >
                            {pageNum}
                          </button>
                        ))}
                      </div>

                      {/* Next button — disabled when on the last known page */}
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= maxPage || isLoading}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-blue-700 hover:bg-blue-50 rounded transition-colors duration-200 disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      >
                        Next
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No Results Message */}
      {showResults && serpResults.length === 0 && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-8 text-center">
          <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-800 dark:text-slate-100 mb-2">No results found</h3>
          <p className="text-gray-600 dark:text-slate-400">Try searching with different keywords or check your internet connection.</p>
        </div>
      )}
    </main>
  );
};

export default SerpChecker;
